import * as React from "react";
import Big from "big.js";
import { ColumnType } from "antd/lib/table";
import moment from "moment";
import { Space, Typography } from "antd";
import Tooltip from "@funds/pages/MyFunds/parts/Tooltip";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import { StakingResponse } from "@services/Funds.service";
import { ActivationTimeBox } from "./parts/ActivationTimeBox/ActivationTimeBox";

import * as styles from "./PendingStakedEthStyle.module.scss";

const StatusMapper: Record<string, any> = {
  staked: {
    label: "ACTIVE",
    color: "rgba(46, 201, 115, 0.2)",
  },
  processing: {
    label: "VALIDATOR IN THE QUEUE",
    color: "#FFE1C6",
  },
  completed: {
    label: "COMPLETED",
    color: "rgba(46, 201, 115, 0.2)",
  },
  queued: {
    label: "IN BF QUEUE",
    color: "#faecff",
  },
};

const big = Big();

const mapStatusToProcessing = (status: string) =>
  ["provisioned", "funded", "contract_created", "deposited"].includes(status)
    ? "processing"
    : status;

export const stakingColumns: ColumnType<StakingResponse>[] = [
  {
    title: "DATE",
    dataIndex: "created_at",
    key: "created_at",
    ellipsis: true,
    width: "15%",
    sorter: (a: any, b: any) =>
      moment(a.created_at).unix() - moment(b.created_at).unix(),
    render: (text: string) => moment(text).format("DD MMM, YYYY"),
  },
  {
    title: "STATUS",
    dataIndex: "status",
    key: "status",
    ellipsis: true,
    width: "15%",
    sorter: (a: StakingResponse, b: StakingResponse) =>
      a.status.localeCompare(b.status),
    render: (text: string) => (
      <Typography.Text
        style={{
          backgroundColor: StatusMapper[mapStatusToProcessing(text)]?.color,
          textTransform: "uppercase",
          padding: "5px 15px 5px 15px",
          borderRadius: "16px",
          fontSize: "11px",
          fontWeight: 600,
        }}
      >
        {StatusMapper[mapStatusToProcessing(text)]?.label}
      </Typography.Text>
    ),
  },
  {
    title: "TIMELOCK",
    dataIndex: "timelock_duration",
    key: "timelock_duration",
    width: "20%",
    sorter: (a: StakingResponse, b: StakingResponse) =>
      a.status.localeCompare(b.status),
    render: (text: string | null) => {
      if (text) {
        const label = text.toString() === "1" ? "year" : "years";

        return `${text} ${label}`;
      }

      return <Typography.Text>NO</Typography.Text>;
    },
  },
  {
    title: "LOCKED UNTIL",
    dataIndex: "timelock_expiry",
    key: "timelock_expiry",
    width: "25%",
    sorter: (a: StakingResponse, b: StakingResponse) =>
      a.status.localeCompare(b.status),
    render: (text: string) => {
      if (!text) {
        return "-";
      }

      return moment(text).format("DD MMM, YYYY");
    },
  },
  {
    title: (
      <Tooltip
        title="VALIDATOR ETA"
        tooltipMessage="Estimated time for the Validator to become Active"
      />
    ),
    dataIndex: "estimated_activation_time",
    key: "estimated_activation_time",
    align: "left",
    width: "25%",
    render: (text: string, row) => {
      console.log(text, row);

      if (!text || row.status === "staked") {
        return "-";
      }

      const calculateRemaningHoursFromDate = moment(text).diff(
        moment(),
        "hours"
      );

      const days = Math.floor(calculateRemaningHoursFromDate / 24);
      const remainingHours = calculateRemaningHoursFromDate % 24;
      const daysLabel = days === 1 ? "day" : "days";
      const hoursLabel = remainingHours === 1 ? "hr" : "hrs";

      if (days <= 0 && remainingHours <= 0) {
        return "-";
      }

      return (
        <Space className={styles.activationTimeBoxWrapper}>
          <ActivationTimeBox value={days.toString()} label={daysLabel} />
          <span>:</span>
          <ActivationTimeBox
            value={remainingHours.toString()}
            label={hoursLabel}
          />
        </Space>
      );
    },
  },
  {
    title: "AMOUNT",
    dataIndex: "amount",
    key: "amount",
    ellipsis: true,
    align: "right",
    width: "30%",
    sorter: (a: StakingResponse, b: StakingResponse) =>
      big(a.total_usd ?? 0).gt(b.total_usd ?? 0) ? 0 : 1,
    render: (text: string, row: StakingResponse) => (
      <>
        <Space style={{ justifyContent: "center" }}>
          <Typography.Text>
            <CurrencyElement value={text} currency={"staked_ETH"} />
          </Typography.Text>
        </Space>
        <br />
        <Typography.Text
          style={{
            paddingLeft: "25px",
            color: "#a2a2a2",
            fontSize: "12px",
            margin: 0,
          }}
        >
          <CurrencyElement value={row.total_usd} currency={"usd"} />
        </Typography.Text>
      </>
    ),
  },
];
